import { useSubscription } from "@/hooks/useSubscription";
import { usePendingArchives } from "@/hooks/usePendingArchives";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { openBillingPortal } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { TIER_DISPLAY_NAMES, type Tier } from "@/lib/tier";
import { toast } from "sonner";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

export const PendingArchiveBanner = () => {
  const { hasPendingDowngrade, pendingTier, pendingEffectiveAt, refresh: refreshSub } = useSubscription();
  const { archives, refresh: refreshArchives } = usePendingArchives();
  const { workspaces, refresh: refreshWs } = useWorkspace();
  const [cancelling, setCancelling] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!hasPendingDowngrade || !pendingTier || !pendingEffectiveAt) return null;

  const date = pendingEffectiveAt.toLocaleDateString();
  const archiveLabels = archives
    .map((a) => workspaces.find((w) => w.id === a.workspace_id)?.workspace_label)
    .filter(Boolean) as string[];

  const cancel = async () => {
    if (!confirm("Cancel the scheduled downgrade? Your current plan and workspaces will stay as-is.")) return;
    setCancelling(true);
    try {
      const { error } = await supabase.functions.invoke("cancel-pending-downgrade", {
        body: { environment: getStripeEnvironment() },
      });
      if (error) throw new Error(error.message);
      toast.success("Downgrade cancelled.");
      await Promise.all([refreshSub(), refreshArchives(), refreshWs()]);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  const reasons = new Set(archives.map((a) => a.reason));
  const isAddonRemoval = reasons.has("addon_removed") && !reasons.has("downgrade");

  return (
    <div className="bg-yellow-500/15 border-b border-yellow-500/40 px-4 py-2.5 text-sm space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span>
          {isAddonRemoval ? (
            <>Your workspace add-on cancellation takes effect on <strong>{date}</strong></>
          ) : (
            <>Your plan changes to <strong>{TIER_DISPLAY_NAMES[pendingTier as Tier]}</strong> on <strong>{date}</strong></>
          )}
          {archiveLabels.length > 0 && (
            <> and these workspaces will be permanently deleted: <strong>{archiveLabels.join(", ")}</strong>. Download their data first.</>
          )}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {archives.length > 0 ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/downgrade-review">Review items</Link>
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={cancel} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Cancel downgrade"}
          </Button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setDetailsOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        aria-expanded={detailsOpen}
      >
        {detailsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        What this means
      </button>

      {detailsOpen && (
        <div className="space-y-2 pl-4">
          <ul className="list-disc space-y-1 text-xs text-muted-foreground">
            <li>You keep full access to every workspace and feature until <strong>{date}</strong>.</li>
            {isAddonRemoval ? (
              <li>Your add-on subscription stops billing after <strong>{date}</strong> — no further charges, no proration owed.</li>
            ) : (
              <li>Your plan changes to <strong>{TIER_DISPLAY_NAMES[pendingTier as Tier]}</strong> after <strong>{date}</strong>. Stripe issues any prorated credit toward your next invoice automatically.</li>
            )}
            {archiveLabels.length > 0 && (
              <li>The listed workspaces become read-only on that date and are permanently deleted 30 days later.</li>
            )}
            <li>Cancelling the downgrade above keeps everything as-is — no charge.</li>
          </ul>
          <button
            type="button"
            onClick={() => void openBillingPortal()}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Manage subscription in billing portal <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};
